// Deliberate duplicate of town-square/src/hooks/useImageVisibility.ts (ADR 0002). A change here belongs there too.
// Client has no `useOwnerSave`, so the per-key pending flag and the failure toast live inline.
import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAnalytics } from '~/context/analytics';
import { UPDATE_IMAGE_MUTATION } from '~/graphql/mutations';

type SetPublicInput = {
  imageId: string;
  isPublic: boolean;
  /** Feeds the success toast; falls back to "Untitled" when the artwork has no title. */
  title?: string | null;
};

function keyOf(imageId: string) {
  return `visibility-${imageId}`;
}

/**
 * One-image visibility flip through the shipped `updateImage` mutation.
 *
 * `updateImage` returns `id` + `isPublic`, so Apollo merges the new value into
 * any cached `Image` by id and every chip reading that entry re-renders.
 * The analytics event fires only on confirmed success.
 */
export function useImageVisibility() {
  const { t } = useTranslation('artwork');
  const { amplitude } = useAnalytics();
  const [updateImage] = useMutation(UPDATE_IMAGE_MUTATION);
  const [pending, setPending] = useState<string[]>([]);

  const setPublic = useCallback(
    async ({ imageId, isPublic, title }: SetPublicInput): Promise<boolean> => {
      const key = keyOf(imageId);
      setPending((keys) => {
        if (keys.includes(key)) {
          return keys;
        }
        return [...keys, key];
      });
      try {
        const { data } = await updateImage({ variables: { input: { id: imageId, isPublic } } });
        if (!data?.updateImage) {
          throw new Error('updateImage returned nothing');
        }
        const name = title || t('image-manager.visibility.untitled');
        if (isPublic) {
          toast.success(t('image-manager.visibility.now-public', { title: name }));
        }
        if (!isPublic) {
          toast.success(t('image-manager.visibility.now-private', { title: name }));
        }
        amplitude.track('arty_artwork_visibility_toggled', { image_id: imageId, is_public: isPublic });
        return true;
      } catch {
        toast.error(t('image-manager.visibility.update-failed'));
        return false;
      } finally {
        setPending((keys) => {
          return keys.filter((row) => {
            return row !== key;
          });
        });
      }
    },
    [amplitude, t, updateImage]
  );

  const isSavingKey = useCallback(
    (key: string) => {
      return pending.includes(key);
    },
    [pending]
  );

  return { setPublic, isSaving: pending.length > 0, isSavingKey };
}
